<template>
  <div class="container-fluid py-3">
    <div class="d-flex align-items-center mb-3 flex-wrap" style="gap:8px">
      <h4 class="mb-0 mr-3">Accounts Manager</h4>
      <button class="btn btn-primary btn-sm" @click="showAddForm = !showAddForm">
        {{ showAddForm ? '✕ Cancel' : '+ Add Account' }}
      </button>
      <button class="btn btn-outline-info btn-sm ml-auto" @click="fetchAllBalances" :disabled="loadingBalances">
        <span v-if="loadingBalances" class="spinner-border spinner-border-sm mr-1" role="status"></span>
        <span v-else>↻</span> Refresh Values
      </button>
      <span v-if="lastUpdated" class="text-muted small">{{ lastUpdated }}</span>
    </div>

    <!-- Total portfolio value banner -->
    <div v-if="totalUsd > 0" class="portfolio-banner mb-3">
      <span class="banner-label">Total Portfolio Value</span>
      <span class="banner-value">{{ formatUsd(totalUsd) }}</span>
      <span class="banner-accounts">{{ accounts.length }} accounts · {{ connectedChains }} chains</span>
    </div>

    <!-- Add account form -->
    <div v-if="showAddForm" class="card mb-4" style="max-width:480px">
      <div class="card-body">
        <h6 class="card-title">New Account</h6>
        <div class="form-group">
          <label>Label</label>
          <input class="form-control" v-model="form.label" placeholder="e.g. Tron Main" />
        </div>
        <div class="form-group">
          <label>Chain</label>
          <select class="form-control" v-model="form.chain">
            <option v-for="c in chains" :key="c.key" :value="c.key">{{ c.label }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>HD Index</label>
          <input class="form-control" type="number" min="0" v-model.number="form.hdIndex" />
        </div>
        <div class="form-group">
          <label>Address (optional — will be filled after Ledger connect)</label>
          <input class="form-control" v-model="form.address" placeholder="Leave blank to fill later" />
        </div>
        <button class="btn btn-primary" @click="addAccount">Save</button>
      </div>
    </div>

    <!-- Accounts table -->
    <table class="table table-striped table-sm">
      <thead>
        <tr>
          <th>Label</th>
          <th>Chain</th>
          <th>HD</th>
          <th>Address</th>
          <th>Balance</th>
          <th>Value</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="accounts.length === 0">
          <td colspan="7" class="text-center text-muted py-4">
            No saved accounts yet. Connect Ledger on any chain page to auto-save, or add manually above.
          </td>
        </tr>
        <tr v-for="acct in accounts" :key="acct.id">
          <td>
            <span v-if="editId !== acct.id">{{ acct.label }}</span>
            <input v-else class="form-control form-control-sm" v-model="editLabel" style="width:140px" />
          </td>
          <td>
            <span class="badge" :style="`background:${chainColor(acct.chain)}; color:#fff; font-size:0.7rem`">
              {{ chainLabel(acct.chain) }}
            </span>
          </td>
          <td>{{ acct.hdIndex }}</td>
          <td class="small text-break" style="max-width:160px">
            {{ acct.address ? acct.address.slice(0, 14) + '…' : '—' }}
          </td>
          <td class="small mono-val">
            <span v-if="balanceLoading[acct.id]" class="spinner-border spinner-border-sm text-info" style="width:10px;height:10px"></span>
            <span v-else-if="balances[acct.id] != null">
              {{ formatBalance(balances[acct.id]) }} {{ nativeTicker(acct.chain) }}
            </span>
            <span v-else class="text-muted">—</span>
          </td>
          <td class="small">
            <span v-if="usdValues[acct.id] != null" class="usd-val">{{ formatUsd(usdValues[acct.id]) }}</span>
            <span v-else class="text-muted">—</span>
          </td>
          <td>
            <button class="btn btn-success btn-sm mr-1" @click="goTo(acct)">Go</button>
            <span v-if="editId !== acct.id">
              <button class="btn btn-secondary btn-sm mr-1" @click="startEdit(acct)">Edit</button>
            </span>
            <span v-else>
              <button class="btn btn-primary btn-sm mr-1" @click="saveEdit(acct)">Save</button>
            </span>
            <button class="btn btn-danger btn-sm" @click="deleteAccount(acct.id)">Del</button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { fetchPrices, formatUsd as fmtUsd } from '../services/priceService';

const CHAIN_META = {
  tron:     { label: 'Tron',    color: '#FF0013', path: '/tron',     ticker: 'TRX'  },
  solana:   { label: 'Solana',  color: '#9945FF', path: '/solana',   ticker: 'SOL'  },
  ethereum: { label: 'ETH',     color: '#627EEA', path: '/ethereum', ticker: 'ETH'  },
  bsc:      { label: 'BSC',     color: '#F0B90B', path: '/bsc',      ticker: 'BNB'  },
  polygon:  { label: 'Polygon', color: '#8247E5', path: '/polygon',  ticker: 'MATIC'},
  bitcoin:  { label: 'Bitcoin', color: '#F7931A', path: '/bitcoin',  ticker: 'BTC'  },
};

// Public RPC endpoints — no API key needed for simple balance queries
async function fetchNativeBalance(address, chain) {
  if (!address) return null;
  try {
    switch (chain) {
      case 'tron': {
        const r = await fetch(`https://api.trongrid.io/v1/accounts/${address}`, { signal: AbortSignal.timeout(6000) });
        const d = await r.json();
        return d.data && d.data.length ? (d.data[0].balance || 0) / 1e6 : 0;
      }
      case 'ethereum': {
        const r = await fetch('https://ethereum-rpc.publicnode.com/', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', method: 'eth_getBalance', params: [address, 'latest'], id: 1 }),
          signal: AbortSignal.timeout(6000),
        });
        const d = await r.json();
        return d.result ? parseInt(d.result, 16) / 1e18 : 0;
      }
      case 'bsc': {
        const r = await fetch('https://bsc-dataseed.binance.org/', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', method: 'eth_getBalance', params: [address, 'latest'], id: 1 }),
          signal: AbortSignal.timeout(6000),
        });
        const d = await r.json();
        return d.result ? parseInt(d.result, 16) / 1e18 : 0;
      }
      case 'polygon': {
        const r = await fetch('https://polygon-rpc.com/', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', method: 'eth_getBalance', params: [address, 'latest'], id: 1 }),
          signal: AbortSignal.timeout(6000),
        });
        const d = await r.json();
        return d.result ? parseInt(d.result, 16) / 1e18 : 0;
      }
      case 'bitcoin': {
        const r = await fetch(`https://blockchain.info/q/addressbalance/${address}?confirmations=1`, { signal: AbortSignal.timeout(8000) });
        const satoshis = await r.text();
        return parseInt(satoshis.trim(), 10) / 1e8;
      }
      case 'solana': {
        const r = await fetch('https://api.mainnet-beta.solana.com/', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', method: 'getBalance', params: [address], id: 1 }),
          signal: AbortSignal.timeout(6000),
        });
        const d = await r.json();
        return d.result ? d.result.value / 1e9 : 0;
      }
      default: return null;
    }
  } catch {
    return null;
  }
}

export default {
  name: 'AccountsManager',
  data() {
    return {
      showAddForm: false,
      editId: null,
      editLabel: '',
      form: { label: '', chain: 'tron', hdIndex: 0, address: '' },
      chains: Object.entries(CHAIN_META).map(([key, m]) => ({ key, label: m.label })),
      balances: {},        // { accountId: number | null }
      balanceLoading: {},  // { accountId: bool }
      prices: {},
      loadingBalances: false,
      lastUpdated: '',
    };
  },
  computed: {
    accounts() {
      return this.$store.state.accounts.accounts;
    },
    usdValues() {
      const result = {};
      for (const acct of this.accounts) {
        const bal = this.balances[acct.id];
        const ticker = CHAIN_META[acct.chain]?.ticker;
        const price = this.prices[ticker];
        if (bal != null && price != null) {
          result[acct.id] = bal * price;
        }
      }
      return result;
    },
    totalUsd() {
      return Object.values(this.usdValues).reduce((s, v) => s + v, 0);
    },
    connectedChains() {
      return new Set(this.accounts.filter(a => this.balances[a.id] != null).map(a => a.chain)).size;
    },
  },
  methods: {
    chainLabel(chain) { return CHAIN_META[chain]?.label || chain; },
    chainColor(chain) { return CHAIN_META[chain]?.color || '#52597a'; },
    nativeTicker(chain) { return CHAIN_META[chain]?.ticker || ''; },
    formatUsd(v) { return fmtUsd(v); },
    formatBalance(v) {
      if (v === null || v === undefined) return '—';
      if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
      if (v >= 0.001) return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
      return v.toFixed(8);
    },
    addAccount() {
      if (!this.form.label) { alert('Please enter a label.'); return; }
      this.$store.commit('accounts/ADD_ACCOUNT', { ...this.form });
      this.form = { label: '', chain: 'tron', hdIndex: 0, address: '' };
      this.showAddForm = false;
    },
    deleteAccount(id) {
      if (confirm('Delete this account?')) {
        this.$store.commit('accounts/DELETE_ACCOUNT', id);
      }
    },
    startEdit(acct) { this.editId = acct.id; this.editLabel = acct.label; },
    saveEdit(acct) {
      this.$store.commit('accounts/UPDATE_LABEL', { id: acct.id, label: this.editLabel });
      this.editId = null;
    },
    goTo(acct) {
      localStorage.setItem(`hd_active_${acct.chain}`, acct.id);
      this.$router.push(CHAIN_META[acct.chain]?.path || '/');
    },
    async fetchAllBalances() {
      this.loadingBalances = true;

      // Fetch prices first
      const tickers = [...new Set(this.accounts.map(a => CHAIN_META[a.chain]?.ticker).filter(Boolean))];
      const { prices } = await fetchPrices(tickers, true);
      this.prices = prices;

      // Fetch balances in parallel, per-account loading indicators
      const settled = await Promise.allSettled(
        this.accounts.map(async acct => {
          if (!acct.address) return { id: acct.id, bal: null };
          this.$set(this.balanceLoading, acct.id, true);
          const bal = await fetchNativeBalance(acct.address, acct.chain);
          this.$set(this.balanceLoading, acct.id, false);
          return { id: acct.id, bal };
        })
      );

      for (const r of settled) {
        if (r.status === 'fulfilled' && r.value) {
          this.$set(this.balances, r.value.id, r.value.bal);
        }
      }

      this.loadingBalances = false;
      this.lastUpdated = new Date().toLocaleTimeString();
    },
  },
  mounted() {
    if (this.accounts.length > 0) this.fetchAllBalances();
  },
};
</script>

<style scoped>
.container-fluid { padding: 0 !important; }
h4 { font-size: 1.1rem !important; font-weight: 800 !important; color: var(--text, #c8cfe8) !important; letter-spacing: -0.01em; }

/* Portfolio banner */
.portfolio-banner {
  background: linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.06));
  border: 1px solid rgba(99,102,241,0.25);
  border-radius: 12px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}
.banner-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-2, #8892b0);
  font-weight: 600;
}
.banner-value {
  font-size: 1.6rem;
  font-weight: 800;
  background: linear-gradient(135deg, #818cf8, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}
.banner-accounts {
  font-size: 0.75rem;
  color: var(--text-2, #8892b0);
  margin-left: auto;
}

/* Add form card */
.card { animation: cardIn 0.2s ease; }
@keyframes cardIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

/* Table */
.table-sm thead th { background: var(--surface-2, #171b28) !important; }
.table tbody tr:last-child td { border-bottom: none !important; }

/* Badge */
.badge { border-radius: 6px !important; font-size: 0.68rem !important; padding: 3px 8px !important; letter-spacing: 0.04em !important; }

/* Address column */
.text-break { font-family: monospace; font-size: 0.78rem !important; color: var(--text-2, #8892b0) !important; }

/* Value cells */
.mono-val { font-family: monospace; font-size: 0.8rem !important; color: var(--text-2, #8892b0) !important; }
.usd-val { color: #6ee7b7; font-weight: 600; font-size: 0.82rem; }

/* Action buttons */
.btn-sm { padding: 4px 10px !important; font-size: 0.75rem !important; }
</style>
